import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import { toast } from "sonner";
import {
  ArrowClockwise,
  Brain,
  CaretDown,
  CaretRight,
  CheckCircle,
  ClipboardText,
  Copy,
  Flask,
  SlidersHorizontal,
  Sparkle,
  UploadSimple,
} from "@phosphor-icons/react";

const STYLES = [
  { key: "first_person", label: "First-Person" },
  { key: "salesforce", label: "Salesforce" },
  { key: "hubspot", label: "HubSpot" },
  { key: "close", label: "Close" },
  { key: "minimal", label: "Minimal" },
  { key: "executive", label: "Executive" },
  { key: "detailed_history", label: "Detailed History" },
];

function VersionPanel({ label, version, generating, temp, setTemp, onRegenerate, onCopy }) {
  return (
    <article className={`sandbox-version${generating ? " is-generating" : ""}`}>
      <div className="sandbox-version__header">
        <div className="sandbox-version__identity">
          <span className="sandbox-version__mark">{label}</span>
          <div className="sandbox-version__identity-copy">
            <div className="sandbox-section-label">Version {label}</div>
            <p>{label === "A" ? "Controlled baseline" : "Alternative interpretation"}</p>
          </div>
          {version?.temperature !== undefined && (
            <span className="sandbox-version__temperature-badge">t={version.temperature.toFixed(2)}</span>
          )}
          {version?.dnaProfile?.calibrated && (
            <span className="sandbox-version__dna-badge">DNA {version.dnaProfile.confidence}%</span>
          )}
        </div>

        <div className="sandbox-version__actions">
          <label className="sandbox-temperature-control">
            <span>Temperature</span>
            <input
              type="range"
              min="0"
              max="1.3"
              step="0.05"
              value={temp}
              onChange={(event) => setTemp(parseFloat(event.target.value))}
              data-testid={`temp-slider-${label.toLowerCase()}`}
            />
            <strong>{temp.toFixed(2)}</strong>
          </label>

          <span className="sandbox-version__buttons">
            <button
              type="button"
              onClick={onRegenerate}
              disabled={generating}
              data-testid={`regen-${label.toLowerCase()}`}
              className="sandbox-primary-button sandbox-primary-button--compact"
            >
              {version ? <ArrowClockwise size={14} weight="bold" /> : <Sparkle size={14} weight="fill" />}
              {generating ? "Working..." : version ? "Regenerate" : "Generate"}
            </button>

            <button
              type="button"
              onClick={onCopy}
              disabled={!version}
              data-testid={`copy-${label.toLowerCase()}`}
              className="sandbox-secondary-button sandbox-secondary-button--compact"
            >
              <Copy size={14} />
              Copy
            </button>
          </span>
        </div>
      </div>

      <div className="segmented-divider" />

      <div className="sandbox-version__output" aria-busy={generating}>
        {generating ? (
          <div className="sandbox-version__placeholder is-working">
            <Sparkle size={22} weight="duotone" />
            <strong>Generating Version {label}</strong>
            <span>Applying the selected style and reasoning temperature.</span>
          </div>
        ) : version ? (
          <div className="sandbox-version__note" data-testid={`note-${label.toLowerCase()}`}>
            {version.note}
          </div>
        ) : (
          <div className="sandbox-version__placeholder">
            <ClipboardText size={22} weight="duotone" />
            <strong>No version generated yet</strong>
            <span>Use Generate to produce Version {label} from the transcript.</span>
          </div>
        )}
      </div>
    </article>
  );
}

export default function SandboxPage() {
  const [transcript, setTranscript] = useState("");
  const [style, setStyle] = useState("first_person");
  const [useProfile, setUseProfile] = useState(true);
  const [profileMeta, setProfileMeta] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [genA, setGenA] = useState(false);
  const [genB, setGenB] = useState(false);
  const [versionA, setVersionA] = useState(null);
  const [versionB, setVersionB] = useState(null);
  const [tempA, setTempA] = useState(0.7);
  const [tempB, setTempB] = useState(1.0);
  const [showDossier, setShowDossier] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    let active = true;
    api.get("/writing-profile")
      .then(({ data }) => {
        if (!active) return;
        const calibrated = Boolean(data?.profile_confidence);
        setProfileMeta({
          calibrated,
          confidence: data?.profile_confidence ?? null,
          maturityLabel: data?.profile_maturity_label || "Not calibrated",
        });
        if (!calibrated) setUseProfile(false);
      })
      .catch(() => {
        if (active) {
          setProfileMeta({ calibrated: false, confidence: null, maturityLabel: "Not calibrated" });
          setUseProfile(false);
        }
      });
    return () => { active = false; };
  }, []);

  const onFile = async (file) => {
    if (!file) return;
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("audio", file);
      const { data } = await api.post("/sandbox/transcribe", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setTranscript(data.transcript || "");
      toast.success("Transcribed");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  const generate = async (slot) => {
    if (!transcript.trim()) return toast.error("Add a transcript first");
    const temp = slot === "A" ? tempA : tempB;
    const setBusy = slot === "A" ? setGenA : setGenB;
    const setVersion = slot === "A" ? setVersionA : setVersionB;
    setBusy(true);
    try {
      const { data } = await api.post("/sandbox/generate", {
        transcript,
        style,
        temperature: temp,
        use_writing_profile: useProfile,
      });
      setVersion({ note: data.note, dossier: data.dossier, temperature: data.temperature, dnaProfile: data.dna_profile });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = (version) => {
    if (!version?.note) return;
    navigator.clipboard.writeText(version.note);
    toast.success("Copied");
  };

  const dossier = versionA?.dossier || versionB?.dossier;
  const selectedStyle = STYLES.find((item) => item.key === style)?.label || style;

  return (
    <div className="sandbox-page">
      <Nav />

      <main className="sandbox-shell">
        <AppBreadcrumbs items={[{ label: "Sandbox" }]} />
        <section className="sandbox-hero">
          <div className="sandbox-hero__copy">
            <div className="brand-eyebrow">Sandbox</div>
            <h1>Prompt playground</h1>
            <p>
              Test the CRM engine on historical calls, compare two temperatures, and inspect the underlying reasoning.
              Nothing in this workspace is saved.
            </p>

            <div className="sandbox-hero__summary" aria-label="Sandbox configuration summary">
              <span><Flask size={15} weight="duotone" /> Isolated workspace</span>
              <span><SlidersHorizontal size={15} weight="duotone" /> {selectedStyle}</span>
              <span><ClipboardText size={15} weight="duotone" /> {transcript.length.toLocaleString()} characters</span>
            </div>
          </div>
        </section>

        <section className="sandbox-workflow" aria-label="Sandbox setup">
          <article className="sandbox-setup-card">
            <div className="sandbox-setup-card__header">
              <span className="sandbox-step-number">01</span>
              <div>
                <div className="sandbox-section-label">Load audio</div>
                <h2>Start from a recording</h2>
              </div>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept=".mp3,.wav,.aac,.m4a,audio/*"
              className="hidden"
              onChange={(event) => onFile(event.target.files?.[0])}
              data-testid="sandbox-file-input"
            />

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className={`sandbox-dropzone${transcribing ? " is-working" : ""}`}
              data-testid="sandbox-dropzone"
              disabled={transcribing}
            >
              <span className="sandbox-dropzone__icon"><UploadSimple size={24} weight="duotone" /></span>
              <strong>{transcribing ? "Transcribing audio..." : "Choose mp3, aac, m4a, or wav"}</strong>
              <small>{transcribing ? "The transcript will appear automatically." : "Optional — you can paste a transcript instead."}</small>
            </button>
          </article>

          <article className="sandbox-setup-card">
            <div className="sandbox-setup-card__header">
              <span className="sandbox-step-number">02</span>
              <div>
                <div className="sandbox-section-label">Configure output</div>
                <h2>Choose the note style</h2>
              </div>
            </div>

            <label className="sandbox-select-label" htmlFor="sandbox-style-select">CRM note format</label>
            <select
              id="sandbox-style-select"
              value={style}
              onChange={(event) => setStyle(event.target.value)}
              data-testid="sandbox-style-select"
              className="sandbox-select"
            >
              {STYLES.map((item) => (
                <option key={item.key} value={item.key}>{item.label}</option>
              ))}
            </select>

            <label className="sandbox-profile-toggle">
              <input
                type="checkbox"
                checked={useProfile}
                onChange={(event) => setUseProfile(event.target.checked)}
                disabled={!profileMeta?.calibrated}
                data-testid="sandbox-use-profile"
              />
              <span className="sandbox-profile-toggle__control" aria-hidden="true" />
              <span>
                <strong>Apply my Writing DNA</strong>
                <small>
                  {profileMeta?.calibrated
                    ? `${profileMeta.maturityLabel} · ${profileMeta.confidence}% profile confidence`
                    : "Not calibrated yet. Add CRM examples in Writing DNA first."}
                </small>
              </span>
            </label>
          </article>

          <article className="sandbox-setup-card sandbox-setup-card--status">
            <div className="sandbox-setup-card__header">
              <span className="sandbox-step-number">03</span>
              <div>
                <div className="sandbox-section-label">Transcript status</div>
                <h2>Ready for generation</h2>
              </div>
            </div>

            <div className="sandbox-transcript-stat">
              <strong>{transcript.length.toLocaleString()}</strong>
              <span>characters loaded</span>
            </div>

            <div className={`sandbox-readiness${transcript.trim() ? " is-ready" : ""}`}>
              <CheckCircle size={16} weight="fill" />
              {transcript.trim() ? "Transcript ready" : "Add audio or paste text"}
            </div>
          </article>
        </section>

        <section className="sandbox-transcript-panel">
          <div className="sandbox-panel-header">
            <div>
              <div className="sandbox-section-label">Source conversation</div>
              <h2>Paste or edit transcript</h2>
              <p>The same transcript is used for both output versions below.</p>
            </div>
            <button
              type="button"
              onClick={() => setTranscript("")}
              data-testid="clear-transcript-btn"
              className="sandbox-secondary-button sandbox-secondary-button--compact"
              disabled={!transcript}
            >
              Clear
            </button>
          </div>

          <div className="segmented-divider" />

          <textarea
            value={transcript}
            onChange={(event) => setTranscript(event.target.value)}
            placeholder="Speaker A: Hi John, thanks for taking the time...\nSpeaker B: Sure — I've been meaning to talk about the renewal..."
            data-testid="sandbox-transcript"
            className="sandbox-transcript-textarea"
          />
        </section>

        {dossier && (
          <section className={`sandbox-dossier${showDossier ? " is-open" : ""}`}>
            <button
              type="button"
              onClick={() => setShowDossier(!showDossier)}
              data-testid="toggle-dossier-btn"
              className="sandbox-dossier__toggle"
              aria-expanded={showDossier}
            >
              <span className="sandbox-dossier__identity">
                <span className="sandbox-dossier__icon"><Brain size={18} weight="duotone" /></span>
                <span>
                  <small>Strategist analysis</small>
                  <strong>Inspect the reasoning behind the note</strong>
                </span>
              </span>
              <span className="sandbox-dossier__state">
                {showDossier ? "Hide" : "Show"}
                {showDossier ? <CaretDown size={15} /> : <CaretRight size={15} />}
              </span>
            </button>

            {showDossier && (
              <div className="sandbox-dossier__content">
                {[
                  ["Real business issue", dossier.real_business_issue],
                  ["Customer's actual concern", dossier.customer_actual_concern],
                  ["Turning point", dossier.turning_point],
                  ["Rep reasoning", dossier.am_reasoning],
                ].map(([label, value]) => value ? (
                  <article key={label} className="sandbox-dossier__item">
                    <div className="sandbox-section-label">{label}</div>
                    <p>{value}</p>
                  </article>
                ) : null)}
              </div>
            )}
          </section>
        )}

        <section className="sandbox-versions">
          <div className="sandbox-versions__intro">
            <div>
              <div className="sandbox-section-label">A/B output lab</div>
              <h2>Compare two interpretations</h2>
              <p>Adjust the temperatures independently to compare precision, tone, and narrative freedom.</p>
            </div>
          </div>

          <div className="sandbox-versions__grid">
            <VersionPanel
              label="A"
              version={versionA}
              generating={genA}
              temp={tempA}
              setTemp={setTempA}
              onRegenerate={() => generate("A")}
              onCopy={() => copy(versionA)}
            />
            <VersionPanel
              label="B"
              version={versionB}
              generating={genB}
              temp={tempB}
              setTemp={setTempB}
              onRegenerate={() => generate("B")}
              onCopy={() => copy(versionB)}
            />
          </div>
        </section>
      </main>
    </div>
  );
}
